import { PythonExecutor, PythonReturnMode } from '../../core/PythonExecutor/PythonExecutor'
import { StateStore } from './StateStore'
import { EventRegistry } from './EventRegistry'
import { BlockEngine } from '../blocks-core/BlockEngine'
import { Logger } from './Logger'
import { PythonModule, ModuleProxy } from './PythonModule'

export class PythonRuntime {
    private executor: PythonExecutor
    private stateStore: StateStore
    private registry: EventRegistry
    private logger: Logger
    private pythonModule: PythonModule
    blockEngine: BlockEngine
    isSetup = false

    constructor(stateStore: StateStore, blockEngine: BlockEngine) {
        this.executor = PythonExecutor.getInstance()
        this.stateStore = stateStore
        this.registry = new EventRegistry()
        this.logger = Logger.getInstance()
        this.blockEngine = blockEngine
        this.pythonModule = new PythonModule(
            this.stateStore,
            this.registry,
            this.logger,
            this.blockEngine
        )
    }

    getEventTypes(blockId: string, blockName?: string): Set<string> {
        return this.registry.getEventTypes(blockId, blockName)
    }

    getPythonModule(): PythonModule {
        return this.pythonModule
    }

    getLogger(): Logger {
        return this.logger
    }

    dispatchEvent(context: {
        blockConfigId: string
        blockConfigName: string
        eventType: string
        eventData: {
            value: React.SyntheticEvent
            itemIndex?: number
            blockId: string
            blockName: string
        }
    }): void {
        const { blockConfigId, blockConfigName, eventType, eventData } = context

        if (!this.registry.has(blockConfigId, eventType, blockConfigName)) {
            return
        }

        this.executeHandlers(blockConfigId, blockConfigName, eventType, eventData).catch(error => {
            this.logger.error('Event dispatch failed', {
                source: 'PythonRuntime',
                error: error instanceof Error ? error : String(error),
                context: { blockConfigId, eventType, eventData },
            })
        })
    }

    async initialize(packages?: string[]): Promise<void> {
        if (this.isSetup) return

        await this.executor.initialize()
        await this.executor.installPackages(packages ?? [])

        const proxy = this.pythonModule.createModuleProxy()
        this.executor.setJsProxy('_fts', proxy)

        const result = await this.executor.runPython(this.getPythonWrapper(), {
            returnMode: PythonReturnMode.StdOut,
        })

        if (result.error) {
            this.logger.error('Failed to setup FTS module', {
                source: 'PythonRuntime',
                error: result.error,
                context: { step: 'initialize' },
            })
            throw new Error(result.error)
        }

        this.isSetup = true
    }

    async execute(code: string): Promise<void> {
        if (!code?.trim()) return

        const result = await this.executor.runPython(code, {
            returnMode: PythonReturnMode.StdOut,
        })

        if (result.error) {
            this.logger.logPythonError('Python code execution failed', result.error, {
                source: 'PythonRuntime',
                pythonCode: code.substring(0, 200),
                context: { executionTime: result.executionTime },
            })
            throw new Error(result.error)
        }

        if (result.result && typeof result.result === 'string' && result.result.trim()) {
            console.log(result.result)
        }
    }

    async executeHandlers(
        blockConfigId: string,
        blockConfigName: string | undefined,
        eventType: string,
        eventData: {
            value: React.SyntheticEvent
            itemIndex?: number
            blockId: string
            blockName: string | undefined
        }
    ): Promise<void> {
        const isInitialized = this.executor.initialized
        if (!isInitialized) {
            console.error('PythonExecutor not initialized')
            return
        }
        try {
            const eventDict = this.executor.toPython({
                blockConfigId,
                blockConfigName,
                eventType,
                eventData,
            })

            const fts = this.executor.getGlobalVariable('fts') as ModuleProxy
            fts._dispatch_event(eventDict)
        } catch (error) {
            this.logger.error('Error executing event handler', {
                source: 'PythonRuntime',
                error: error instanceof Error ? error : String(error),
                context: {
                    blockConfigId,
                    eventType,
                    blockConfigName,
                    eventData,
                },
            })
        }
    }

    destroy(): void {
        this.registry.clear()
        this.pythonModule.clear()
        if (this.isSetup) {
            try {
                this.executor.runPythonSync(
                    '[globals().__delitem__(k) for k in list(globals().keys()) if not k.startswith("__")]'
                )
            } catch (e) {
                // Variable might not exist - ignore
            }
        }
        this.isSetup = false
    }

    private getPythonWrapper(): string {
        return `
# =========================================================
# FTS Module - Python Wrapper
# 
# Design: All logic is in JavaScript (_fts proxy).
# Python only stores handler functions (can't serialize to JS).
# =========================================================
import asyncio
import inspect

class _FTS:
    """
    FTS API for Python.
    
    Usage:
        from fts import fts
        
        # State
        fts.state.get('key')
        fts.state.set('key', value)
        
        # Events (decorator)
        @fts.on('button-name', 'onClick')
        def handle_click(event):
            fts.ui.showMessage('Clicked!')
        
        # Events (API)
        fts.addListener('button-name', 'onClick', lambda e: print('clicked'))
        
        # Extensions (dynamically available based on registered extensions)
        # Example: fts.violations.add('error-id', 'Error message', 'error')

        # Block methods - by ID
        fts.block.getById('video-block-id').playSegment(10.5, 25.3)
        fts.block.getById('video-block-id').setSegment(5.0, 15.0)
        fts.block.getById('video-block-id').seekToTime(12.5)
        [start, end] = fts.block.getById('video-block-id').getCurrentSegment()
        
        # Block methods - by name
        fts.block.getByName('video_player').playSegment(10.5, 25.3)
        fts.block.getByName('video_player').setSegment(5.0, 15.0)
        
        # Editable state
        fts.editable.get()       # True if task is editable, False if read-only
        fts.editable.set(True)   # Make task editable
        fts.editable.set(False)  # Make task read-only
    """
    
    def __init__(self):
        self._handlers = {}
        # Delegate to JS proxy
        self.state = _fts.state
        self.ui = _fts.ui
        self.log = _fts.log
        self.block = _fts.block
        self.editable = _fts.editable
    
    # ========== Event Handlers ==========
    
    def _store_handler(self, sel_type, sel_value, event_type, handler):
        """Store handler in Python and notify JS"""
        import uuid
        handler_id = str(uuid.uuid4())
        key = f"{sel_type}:{sel_value}:{event_type}"
        self._handlers[handler_id] = {
            'key': key,
            'handler': handler
        }
        # Register handler ID with JS (not the function itself)
        _fts.events.register(sel_type, sel_value, event_type, handler_id)
        print(f"[Python] Handler registered: {key} -> {handler_id}")
        return handler_id
    
    def _get_handlers(self, sel_type, sel_value, event_type):
        """Get handlers for a specific event"""
        key = f"{sel_type}:{sel_value}:{event_type}"
        return [h['handler'] for h in self._handlers.values() if h['key'] == key]
    
    def _dispatch_event(self, event):
        """Dispatch event to handlers (called by JS on each event)"""
        block_config_id = event.get('blockConfigId')
        block_config_name = event.get('blockConfigName')
        event_type = event.get('eventType')
        
        # Collect handlers
        handlers = self._get_handlers('id', block_config_id, event_type)
        if block_config_name:
            handlers.extend(self._get_handlers('name', block_config_name, event_type))
        event_data = event.get('eventData', {})
        # Execute handlers - await if async
        for handler in handlers:
             result = handler(event_data)
             #Check if handler returned a coroutine and await it
             if inspect.iscoroutine(result):
                 asyncio.create_task(result)

    
    # ========== API Style ==========
    
    def addListener(self, target=None, event=None, handler=None, *, id=None, name=None):
        """Add event listener (API style)"""
        # Resolve selector
        if target is not None and id is None and name is None:
            sel_type, sel_value = 'name', target
        elif id is not None:
            sel_type, sel_value = 'id', id
        elif name is not None:
            sel_type, sel_value = 'name', name
        else:
            raise ValueError("Must specify target, id or name")
        
        if not event:
            raise ValueError("event is required")
        if not handler:
            raise ValueError("handler is required")
        
        return self._store_handler(sel_type, sel_value, event, handler)
    
    def removeListener(self, handler_id):
        """Remove event listener by handler_id"""
        if handler_id in self._handlers:
            info = self._handlers[handler_id]
            parts = info['key'].split(':')
            _fts.events.unregister(parts[0], parts[1], parts[2], handler_id)
            del self._handlers[handler_id]
    
    def __getattr__(self, name):
        """Dynamically delegate to extensions registered in JS proxy"""
        if hasattr(_fts, name):
            return getattr(_fts, name)
        raise AttributeError(f"'{self.__class__.__name__}' object has no attribute '{name}'")


# Create singleton - globally available, no import needed
fts = _FTS()

print("[FTS] Ready")
`
    }
}
